// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

/**
 * D-HOME-2 click-through — metric card → prescription list deep link.
 *
 * A home metric card counts rows server-side (HomeMetricFunctions, allowlisted
 * SQL). Clicking the card must show the user THOSE rows. We encode the card's
 * filter set into a `mf` URL param on /rx; StationQueue decodes it and replays
 * the same predicates client-side over the loaded list, pinned as a dismissible
 * chip. The predicate here MUST mirror the server's SQL semantics
 * (HomeMetricSql.Build): same ops, case-insensitive text equality (SQL default
 * collation), numeric coercion for numeric fields, window = field >= now-N days.
 *
 * The payload is display/filter config only — never PHI, never SQL. The list
 * endpoint still enforces auth + tenant scoping exactly as before; a tampered
 * `mf` can only change which of the rows THE USER ALREADY SEES are shown.
 */

export interface MetricLinkFilter { field: string; op: string; values?: (string | number)[] }
export interface MetricLinkWindow { field: string; last_days?: number; amount?: number; mode?: 'within' | 'older_than'; unit?: 'days' | 'hours' }
export interface MetricLinkPayload {
  t: string                                  // card title (chip label)
  f: MetricLinkFilter[]                      // filters (server spec shape)
  w?: MetricLinkWindow                       // optional time window (D-HOME-3: mode/unit)
}

const NUMERIC_FIELDS = new Set(['refills_remaining', 'days_supply', 'quantity_authorized'])
const DATE_FIELDS = new Set(['created_at', 'last_state_change_at', 'date_filled_utc'])
const TEXT_FIELDS = new Set(['state', 'payer_state', 'pioneerrx_rx_status', 'drug_name'])

// base64url so the payload survives URL encoding untouched.
export function encodeMetricLink(p: MetricLinkPayload): string {
  const b64 = btoa(unescape(encodeURIComponent(JSON.stringify(p))))
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export function decodeMetricLink(s: string): MetricLinkPayload | null {
  try {
    const b64 = s.replace(/-/g, '+').replace(/_/g, '/')
    const p = JSON.parse(decodeURIComponent(escape(atob(b64))))
    if (typeof p?.t !== 'string' || !Array.isArray(p?.f)) return null
    return p as MetricLinkPayload
  } catch { return null }
}

/** Build the /rx deep link for a metric spec; `extra` appends a group-tile filter. */
export function metricRxLink(
  title: string,
  metric: { filters?: MetricLinkFilter[]; window?: MetricLinkWindow; group_by?: string },
  extra?: { group_by: string; group: string },
): string {
  const f: MetricLinkFilter[] = [...(metric.filters ?? [])]
  if (extra) {
    // '(none)' is the server's label for NULL/blank group values.
    f.push(extra.group === '(none)'
      ? { field: extra.group_by, op: 'is_null' }
      : { field: extra.group_by, op: 'eq', values: [extra.group] })
  }
  const payload: MetricLinkPayload = { t: title, f, ...(metric.window ? { w: metric.window } : {}) }
  return `/rx?mf=${encodeMetricLink(payload)}`
}

/** Client replay of the server predicate over a loaded Rx row. */
export function metricRowPredicate(p: MetricLinkPayload): (row: any) => boolean {
  // D-HOME-3: window supports days|hours and within (>= cutoff) | older_than (<= cutoff),
  // mirroring the server's DATEADD comparison exactly.
  const amt = p.w ? (p.w.amount ?? p.w.last_days ?? 0) : 0
  const ms = p.w?.unit === 'hours' ? 3_600_000 : 86_400_000
  const cutoff = p.w ? Date.now() - amt * ms : null
  const older = p.w?.mode === 'older_than'
  return (row: any) => {
    if (cutoff !== null && p.w) {
      const raw = row[p.w.field]
      const t = raw ? Date.parse(raw) : NaN
      if (Number.isNaN(t)) return false
      if (older ? !(t <= cutoff) : !(t >= cutoff)) return false
    }
    for (const f of p.f) {
      const raw = row[f.field]
      const isNull = raw === null || raw === undefined || raw === ''
      if (f.op === 'is_null') { if (!isNull) return false; continue }
      if (f.op === 'not_null') { if (isNull) return false; continue }
      if (isNull) return false
      if (NUMERIC_FIELDS.has(f.field)) {
        const rv = Number(raw)
        const fv = Number(f.values?.[0])
        if (Number.isNaN(rv) || Number.isNaN(fv)) return false
        switch (f.op) {
          case 'eq': if (rv !== fv) return false; break
          case 'ne': if (rv === fv) return false; break
          case 'gt': if (!(rv > fv)) return false; break
          case 'gte': if (!(rv >= fv)) return false; break
          case 'lt': if (!(rv < fv)) return false; break
          case 'lte': if (!(rv <= fv)) return false; break
          case 'in': if (!(f.values ?? []).some(v => Number(v) === rv)) return false; break
          default: return false
        }
      } else if (TEXT_FIELDS.has(f.field) || DATE_FIELDS.has(f.field)) {
        // Text: mirror SQL's case-insensitive default collation.
        const rv = String(raw).trim().toLowerCase()
        const vals = (f.values ?? []).map(v => String(v).trim().toLowerCase())
        switch (f.op) {
          case 'eq': if (rv !== vals[0]) return false; break
          case 'ne': if (rv === vals[0]) return false; break
          case 'in': if (!vals.includes(rv)) return false; break
          default: return false
        }
      } else {
        return false // unknown field — fail closed, mirroring the server allowlist
      }
    }
    return true
  }
}
